import React from 'react';
import { Users, AlertTriangle } from 'lucide-react';

const EscalationMonitorPanel = ({ requests }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getEscalationLevelColor = (level) => {
    if (level >= 3) return 'text-red-600 bg-red-100';
    if (level >= 2) return 'text-orange-600 bg-orange-100';
    return 'text-yellow-600 bg-yellow-100';
  };

  if (!requests || requests?.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Users className="h-6 w-6 text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-900">Escalation Monitor</h2>
        </div>
        <p className="text-gray-600 text-center py-8">No escalated requests at this time.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Users className="h-6 w-6 text-orange-600" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Escalation Monitor</h2>
              <p className="text-sm text-gray-600">{requests?.length} escalated requests</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5 text-orange-600" />
            <span className="text-sm font-medium text-orange-600">Active Escalations</span>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="space-y-4">
          {requests?.map((request) => (
            <div
              key={request?.id}
              className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      getEscalationLevelColor(request?.escalationLevel)
                    }`}>
                      Level {request?.escalationLevel} Escalation
                    </span>
                    {request?.isOverdue && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        Overdue {request?.overdueDays}d
                      </span>
                    )}
                  </div>

                  <h3 className="text-sm font-semibold text-gray-900 mb-1">
                    {request?.employeeName} - {request?.documentType?.replace('_', ' ')?.toUpperCase()}
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">
                    {request?.employeeNumber} • {request?.department} • Expires: {new Date(request?.currentExpiryDate)?.toLocaleDateString()}
                  </p>

                  {/* Escalation Timeline */}
                  <div className="space-y-2">
                    {request?.managerNotified && (
                      <div className="flex items-center space-x-2 text-sm">
                        <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                        <span className="text-gray-700 font-medium">Manager Notified:</span>
                        <span className="text-gray-600">{formatDate(request?.managerNotifiedAt)}</span>
                        {request?.managerEmail && (
                          <span className="text-gray-500">({request?.managerEmail})</span>
                        )}
                      </div>
                    )}

                    {request?.departmentHeadNotified && (
                      <div className="flex items-center space-x-2 text-sm">
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                        <span className="text-gray-700 font-medium">Department Head Notified:</span>
                        <span className="text-gray-600">{formatDate(request?.departmentHeadNotifiedAt)}</span>
                      </div>
                    )}

                    {request?.hrNotified && (
                      <div className="flex items-center space-x-2 text-sm">
                        <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                        <span className="text-gray-700 font-medium">HR Escalated:</span>
                        <span className="text-gray-600">{formatDate(request?.hrNotifiedAt)}</span>
                        {request?.hrAssignedTo && (
                          <span className="text-gray-500">(Assigned to: {request?.hrAssignedTo})</span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Escalation History */}
                  {request?.escalationHistory && request?.escalationHistory?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-xs font-medium text-gray-700 mb-2">Escalation History:</p>
                      <div className="space-y-1">
                        {request?.escalationHistory?.map((history, index) => (
                          <div key={index} className="text-xs text-gray-600">
                            <span className="font-medium">Level {history?.level}:</span> {history?.reason} - {formatDate(history?.timestamp)}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="ml-4 flex flex-col items-end space-y-2">
                  <div className="text-right">
                    <div className="text-xs text-gray-500">Days Until Expiry</div>
                    <div className={`text-2xl font-bold ${
                      request?.daysUntilExpiry <= 7 ? 'text-red-600' :
                      request?.daysUntilExpiry <= 14 ? 'text-orange-600': 'text-gray-900'
                    }`}>
                      {request?.daysUntilExpiry}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default EscalationMonitorPanel;